import { TONES } from "../../components/filters/statusColors";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, m } from "framer-motion";
import { createPortal } from "react-dom";
import {
  Briefcase,
  Building2,
  Check,
  Copy,
  ExternalLink,
  Mail,
  MapPin,
  MessageCircle,
  Pencil,
  Phone,
  Users,
  X,
} from "lucide-react";

import { staffApi } from "../../api/staffApi";
import { useAuth } from "../../auth/AuthProvider";
import { Avatar } from "../common/Avatar";
import { Tooltip } from "../common/Tooltip";
import { useMemberCard } from "./MemberCardContext";

type CardMember = {
  id: string;
  firstName?: string;
  lastName?: string;
  designation?: string | null;
  email?: string;
  mobile?: string | null;
  isActive?: boolean;
  profilePicUrl?: string;
  businessUnitName?: string;
  departmentName?: string;
  reportingManagerName?: string;
  city?: string | null;
  state?: string | null;
  roleNames?: string[];
};

/**
 * The visiting card behind every MemberLink.
 *
 * Mounted once, near the app root, and driven by MemberCardContext — see the
 * note there for why this is not per-caller state. It shows the summary a
 * person needs to act on a name they met in a contract role, an audit row or
 * a comment: who they are, how to reach them, and where they sit in the
 * organisation. Anything beyond that belongs on the full profile, which the
 * footer links to.
 */
export function GlobalMemberCard() {
  const ctx = useMemberCard();
  const { t } = useTranslation("common");
  const { user } = useAuth();

  const [member, setMember] = useState<CardMember | null>(null);
  /** Which row was last copied, so the tick is shown on that row only. */
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const memberId = ctx?.state.memberId ?? null;
  const fallbackName = ctx?.state.fallbackName;

  useEffect(() => {
    if (!memberId) return;

    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setMember(null);

    staffApi
      .searchStaff({ ids: [memberId], pageIndex: 0, pageSize: 1 })
      .then(async (res: any) => {
        if (cancelled) return;
        const found = (res?.data || res?.items || [])[0];
        if (!found) {
          setFailed(true);
          return;
        }

        // profilePic is a storage key, not a URL — the staff slice resolves it
        // through getMediaLink and this card has to do the same or it shows
        // initials for a member who has uploaded a photo.
        if (found.profilePic && !String(found.profilePic).startsWith("http")) {
          try {
            const links = await staffApi.getMediaLink(
              [found.profilePic],
              1,
              found.groupId,
            );
            found.profilePicUrl = links?.links?.[0]?.link || "";
          } catch {
            // Falls back to initials — not worth failing the card over.
          }
        } else if (found.profilePic) {
          found.profilePicUrl = found.profilePic;
        }

        if (!cancelled) setMember(found);
      })
      .catch(() => {
        // A member outside the caller's scope reads as "can't show this",
        // not as an error worth a toast — the name is still on screen behind
        // the card, and the card is an aside.
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [memberId]);

  useEffect(() => {
    if (!memberId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") ctx?.closeMemberCard();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [memberId, ctx]);

  const copyValue = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedLabel(label);
      setTimeout(() => setCopiedLabel(null), 1500);
    } catch {
      // Clipboard is permission-gated and blocked outright in some embedded
      // contexts; silently leaving the value on screen to select by hand is
      // better than an error for something this incidental.
    }
  };

  if (!ctx) return null;

  const open = !!memberId;
  const name =
    [member?.firstName, member?.lastName].filter(Boolean).join(" ").trim() ||
    fallbackName ||
    "—";

  // The Edit affordance is the caller's own record only. Editing anyone else
  // goes through the staff pages, which apply their own permission checks.
  const isSelf = !!memberId && user?.currentLoggedInUserId === memberId;

  const location = [member?.city, member?.state].filter(Boolean).join(", ");

  const rows: Array<{
    icon: React.ReactNode;
    label: string;
    value?: string | null;
    /** Rendered as a mailto/tel link rather than plain text. */
    href?: string;
    /** An extra control beside the copy button — WhatsApp on the phone row. */
    extra?: { href: string; title: string; icon: React.ReactNode };
  }> = [
    {
      icon: <Mail size={12} />,
      label: t("memberCard.email", { defaultValue: "Email" }),
      value: member?.email,
      href: member?.email ? `mailto:${member.email}` : undefined,
    },
    {
      icon: <Phone size={12} />,
      label: t("memberCard.phone", { defaultValue: "Phone" }),
      value: member?.mobile,
      href: member?.mobile ? `tel:${member.mobile}` : undefined,
      extra: member?.mobile
        ? {
            // wa.me takes digits only — no spaces, dashes or leading plus.
            href: `https://wa.me/${String(member.mobile).replace(/[^0-9]/g, "")}`,
            title: t("memberCard.whatsapp", {
              defaultValue: "Message on WhatsApp",
            }),
            icon: <MessageCircle size={12} />,
          }
        : undefined,
    },
    {
      icon: <Briefcase size={12} />,
      label: t("memberCard.designation", { defaultValue: "Designation" }),
      value: member?.designation,
    },
    {
      icon: <Building2 size={12} />,
      label: t("memberCard.businessUnit", { defaultValue: "Business Unit" }),
      value: member?.businessUnitName,
    },
    {
      icon: <Users size={12} />,
      label: t("memberCard.department", { defaultValue: "Department" }),
      value: member?.departmentName,
    },
    {
      icon: <Users size={12} />,
      label: t("memberCard.reportsTo", { defaultValue: "Reports to" }),
      value: member?.reportingManagerName,
    },
    {
      icon: <MapPin size={12} />,
      label: t("memberCard.location", { defaultValue: "Location" }),
      value: location,
    },
  ].filter((r) => !!r.value);

  return createPortal(
    <AnimatePresence>
      {open && (
        <m.div
          className="fixed inset-0 z-[100000] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => ctx.closeMemberCard()}
          />

          <m.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="
              relative z-10 w-full max-w-sm
              overflow-hidden rounded-2xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--card))] shadow-2xl
            "
          >
            <button
              type="button"
              onClick={() => ctx.closeMemberCard()}
              aria-label={t("memberCard.close", { defaultValue: "Close" })}
              className="
                absolute right-3 top-3 z-10
                flex h-7 w-7 items-center justify-center rounded-full
                bg-white/20 text-white
                hover:bg-white/35 transition
              "
            >
              <X size={15} />
            </button>

            {/* Accent band with the avatar straddling it — the card reads as
                an identity first and a data list second, which is the whole
                point of showing it instead of the full profile page. */}
            <div className="relative h-20 bg-gradient-to-br from-[rgb(var(--accent-fill))] via-[rgb(var(--accent-fill))] to-[rgb(var(--accent-fill))]/80">
              <div className="absolute -bottom-9 left-1/2 -translate-x-1/2">
                <div className="rounded-full ring-4 ring-[rgb(var(--card))]">
                  <Avatar src={member?.profilePicUrl} label={name} size={72} />
                </div>
              </div>
            </div>

            <div className="px-5 pb-4 pt-11 text-center">
              <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))]">
                {name}
              </h3>

              {member?.designation && (
                <p className="mt-0.5 text-xs text-[rgb(var(--text-muted))]">
                  {member.designation}
                </p>
              )}

              {member?.isActive !== undefined && (
                <span
                  className={
                    member.isActive
                      ? `mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONES.success.bg} ${TONES.success.text}`
                      : `mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONES.danger.bg} ${TONES.danger.text}`
                  }
                >
                  <span
                    aria-hidden
                    className={
                      member.isActive
                        ? "h-1.5 w-1.5 rounded-full bg-emerald-500"
                        : "h-1.5 w-1.5 rounded-full bg-red-500"
                    }
                  />
                  {member.isActive
                    ? t("memberCard.active", { defaultValue: "Active" })
                    : t("memberCard.inactive", { defaultValue: "Inactive" })}
                </span>
              )}
            </div>

            <div className="px-5 pb-4">
              {loading && (
                <p className="py-3 text-center text-xs text-[rgb(var(--text-muted))]">
                  {t("memberCard.loading", { defaultValue: "Loading…" })}
                </p>
              )}

              {!loading && failed && (
                <p className="py-3 text-center text-xs text-[rgb(var(--text-muted))]">
                  {t("memberCard.unavailable", {
                    defaultValue:
                      "This member's details aren't available to you.",
                  })}
                </p>
              )}

              {/* Label left, value right on the card surface — the same
                  label/value pairing the detail pages use, so the card does
                  not read as a different product. */}
              {!loading && !failed && rows.length > 0 && (
                <div className="space-y-1.5 rounded-xl bg-[rgb(var(--card-muted))] p-2.5">
                  {rows.map((row, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-2"
                    >
                      <span className="flex items-center gap-1.5 text-[11px] text-[rgb(var(--text-muted))]">
                        {row.icon}
                        {row.label}
                      </span>
                      <span className="flex min-w-0 items-center justify-end gap-1">
                        {row.href ? (
                          <a
                            href={row.href}
                            className="min-w-0 flex-1 break-words rounded-lg bg-[rgb(var(--card))] px-2 py-1 text-right text-xs font-medium text-[rgb(var(--text-accent))] hover:underline"
                          >
                            {row.value}
                          </a>
                        ) : (
                          <span className="min-w-0 flex-1 break-words rounded-lg bg-[rgb(var(--card))] px-2 py-1 text-right text-xs font-medium text-[rgb(var(--text-primary))]">
                            {row.value}
                          </span>
                        )}

                        {row.extra && (
                          <Tooltip label={row.extra.title}>
                            <a
                              href={row.extra.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={row.extra.title}
                              className="shrink-0 rounded p-1 text-[rgb(var(--text-muted))] hover:text-emerald-500 transition"
                            >
                              {row.extra.icon}
                            </a>
                          </Tooltip>
                        )}

                        <Tooltip
                          label={t("memberCard.copy", {
                            defaultValue: "Copy {{label}}",
                            label: row.label,
                          })}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              copyValue(row.label, String(row.value))
                            }
                            aria-label={t("memberCard.copy", {
                              defaultValue: "Copy {{label}}",
                              label: row.label,
                            })}
                            className="shrink-0 rounded p-1 text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-accent))] transition"
                          >
                            {copiedLabel === row.label ? (
                              <Check size={12} className="text-emerald-700" />
                            ) : (
                              <Copy size={12} />
                            )}
                          </button>
                        </Tooltip>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 border-t border-[rgb(var(--border))] px-5 py-3">
              {isSelf && (
                <a
                  href={`/admin/staff/${memberId}`}
                  onClick={() => ctx.closeMemberCard()}
                  className="
                    flex flex-1 items-center justify-center gap-1.5
                    rounded-lg bg-[rgb(var(--accent-fill))]
                    py-2 text-xs font-medium text-[rgb(var(--accent-ink))] transition
                  "
                >
                  <Pencil size={13} />
                  {t("memberCard.edit", { defaultValue: "Edit my profile" })}
                </a>
              )}

              <a
                href={`/admin/staff/${memberId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  flex flex-1 items-center justify-center gap-1.5
                  rounded-lg border border-[rgb(var(--border))]
                  bg-[rgb(var(--card-muted))]
                  py-2 text-xs font-medium text-[rgb(var(--text-primary))]
                  transition
                "
              >
                <ExternalLink size={13} />
                {t("memberCard.openProfile", { defaultValue: "Full profile" })}
              </a>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
